import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { useSelector, useDispatch } from 'react-redux';
import { useToasts } from 'common/toast';
import { Icon } from 'semantic-ui-react';
import { Lightbulb, BookOpen, Bookmark } from 'lucide-react';
import { getPlan, addRecommendationToPlan, getSelectedActionPlanTaskKeys } from '@/areas/culture-hub/slices/cultureHubInterventionsSlice';
import { getCurrentOrganizationId, getMemberId, isCurrentMemberTeamLeader } from '@/common/selectors/auth';
import { BodyText, H2, H4 } from '@/common/styles/page';
import { FileTypes, getFileTypeIcon } from '@/common/constants/filetypes';
import { openResourceLink } from '@/common/utils/resourceLinks';
import { isYouTubeWatchUrl } from '@/common/utils/resourceLinks';
import { AppConfig } from '@/Config';
import { authenticatedFetch } from '@/common/utils/authFetch';
import { useCultureHubQueryParams } from '@/areas/culture-hub/hooks/useCultureHubQueryParams';
import { useActionPlanOperations } from '@/areas/culture-hub/hooks/useActionPlanOperations';
import { ACTION_PLAN_TYPE_INTERVENTIONS } from '@/areas/culture-hub/constants';
import { StyledButton } from '@/common/components/button/StyledButton';
import { MobilePageContent, MobilePageSection, Title } from '@/mobile/layouts/common/MobilePageStyles';

const MobileFocusAreaDetail: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const plan = useSelector(getPlan);

  const dispatch = useDispatch();
  const { addToast } = useToasts();
  const stateItem = (location.state && (location.state as any).item) as any | undefined;
  const source = (location.state && (location.state as any).source) as 'pillars' | 'focusAreas' | undefined;
  const isFromPillars = source === 'pillars';
  let item = stateItem;
  const organizationId = useSelector(getCurrentOrganizationId);
  const currentUserId = useSelector(getMemberId) ?? null;
  const isTeamLeader = useSelector(isCurrentMemberTeamLeader);
  const selectedActionPlanTaskKeys = useSelector(getSelectedActionPlanTaskKeys);
  const { assessmentPeriodId, teamId, organizationId: queryOrganizationId, shouldFetchData, isMostRecentClosedAssessmentPeriod } = useCultureHubQueryParams();

  const { saveAllTasks, determineLeaderAndCategory } = useActionPlanOperations({
    selectedAssessmentPeriodId: assessmentPeriodId,
    organizationId: queryOrganizationId,
    currentUserId,
    currentUserDebriefSchedule: null,
    categoryDelegates: [],
    existingActionPlans: undefined,
    actionPlanType: ACTION_PLAN_TYPE_INTERVENTIONS,
    teamId,
  });

  const recordLinkOpen = async (url: string, resourceName?: string, repositoryFileId?: number) => {
    if (!organizationId) {
      return;
    }

    const apiUrl = AppConfig.api.apiUrl || '';
    const endpoint = `${apiUrl}/api/portal/v1/assessments/leaderboard-events/link-open/`;

    try {
      await authenticatedFetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-NULOGIC-ORGANIZATION-ID': String(organizationId),
        },
        body: JSON.stringify({
          url,
          resource_name: resourceName,
          repository_file_id: repositoryFileId,
        }),
      });
    } catch (error) {
      console.warn('Failed to record link open leaderboard event', error);
    }
  };

  if (!item && params.templateId && typeof params.taskIndex !== 'undefined') {
    const idx = Number(params.taskIndex);
    item = plan.find((p: any) => String(p.recommendation_template_id) === String(params.templateId) && Number(p.task_index) === idx);
  }

  if (!item) {
    return (
      <MobilePageContent>
        <MobilePageSection>
          <Title>Recommendation not found</Title>
          <BodyText>No recommendation details were available for this focus area.</BodyText>
        </MobilePageSection>
      </MobilePageContent>
    );
  }

  const referencesText = (typeof item.references === 'string' && item.references.trim()) ? item.references.trim() : undefined;
  const isItemAdded = selectedActionPlanTaskKeys.some(
    (actionPlan) =>
      actionPlan.recommendation_template_id === item.recommendation_template_id &&
      actionPlan.task_index === (item.task_index ?? 0)
  );

  const handleAddToFocusAreas = async () => {
    if (!assessmentPeriodId || !queryOrganizationId) {
      return;
    }

    if (!isMostRecentClosedAssessmentPeriod) {
      addToast('You can only add focus plan items in the latest closed assessment period.', {
        appearance: 'error',
        autoDismiss: true,
      });
      return;
    }

    const { leaderId, categoryUid } = determineLeaderAndCategory();
    dispatch(addRecommendationToPlan(item));

    const result = await saveAllTasks([item], leaderId, categoryUid);
    if (!result.success) {
      addToast(result.error || 'Failed to add focus area', { appearance: 'error', autoDismiss: true });
      return;
    }

    addToast('Focus area added to your plan', { appearance: 'success', autoDismiss: true });
  };

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }

    navigate('/app/mobile/focus-areas/recommendations');
  };

  const isYouTubeResource = (resource: any): boolean =>
    resource?.type === FileTypes.URL && isYouTubeWatchUrl(resource?.object_key ?? undefined);

  return (
    <MobilePageContent>
      <MobilePageSection>
        <TopRow>
          <div>
            <Title>{item.task || item.task_title || item.title || item.recommendation_name || 'Recommendation'}</Title>
            {referencesText ? <SmallCaption>{item.supportingScience ? `${item.supportingScience} • ${referencesText}` : referencesText}</SmallCaption> : null}
          </div>
        </TopRow>

        <SectionBlock>
          <Card>
            <Tile>
              <IconCircle style={{ background: '#f59e0b' }}>
                <Lightbulb className="w-5 h-5 text-white" />
              </IconCircle>
              <div>
                <H2>Overview</H2>
              </div>
            </Tile>
            {item.description && <BodyText>{item.description}</BodyText>}
            {item.practicalExample && (
              <FieldBlock>
                <H4>Practical example</H4>
                <BodyText>“{item.practicalExample}”</BodyText>
              </FieldBlock>
            )}
            {item.softSkills && (
              <FieldBlock>
                <H4>Soft skills</H4>
                <BodyText>{item.softSkills}</BodyText>
              </FieldBlock>
            )}
            {item.areaOfFocus && (
              <FieldBlock>
                <H4>Area of focus</H4>
                <BodyText>{item.areaOfFocus}</BodyText>
              </FieldBlock>
            )}
            {item.processChangeImplementationTime && (
              <FieldBlock>
                <H4>Implementation time</H4>
                <BodyText>{item.processChangeImplementationTime}</BodyText>
              </FieldBlock>
            )}
          </Card>
        </SectionBlock>

        <SectionBlock>
          <Card>
            <Tile>
              <IconCircle style={{ background: '#7c3aed' }}>
                <BookOpen className="w-5 h-5 text-white" />
              </IconCircle>
              <div>
                <H2>Learn More</H2>
              </div>
            </Tile>
            {item.howToImplement && (
              <FieldBlock>
                <H4>How to implement</H4>
                <BodyText>{item.howToImplement}</BodyText>
              </FieldBlock>
            )}
            {item.whatNotToDo && (
              <FieldBlock>
                <H4>What not to do</H4>
                <BodyText>{item.whatNotToDo}</BodyText>
              </FieldBlock>
            )}
            {item.howToKnowItsWorking && (
              <FieldBlock>
                <H4>How to know it's working</H4>
                <BodyText>{item.howToKnowItsWorking}</BodyText>
              </FieldBlock>
            )}
            {item.suggestedProcessChanges && (
              <FieldBlock>
                <H4>Suggested process changes</H4>
                <BodyText>{item.suggestedProcessChanges}</BodyText>
              </FieldBlock>
            )}
          </Card>
        </SectionBlock>

        <SectionBlock>
          <Card>
            <Tile>
              <IconCircle style={{ background: '#10b981' }}>
                <Bookmark className="w-5 h-5 text-white" />
              </IconCircle>
              <div>
                <H2>Resources</H2>
              </div>
            </Tile>
            {Array.isArray(item.content) && item.content.length > 0 ? (
              <ResourceList>
                {item.content.map((c: any, i: number) => {
                  const handleRowAction = (e?: React.SyntheticEvent) => {
                    if (e) e.stopPropagation();

                    openResourceLink(c, {
                      recordLinkOpen: (url, resourceName, repositoryFileId) => {
                        void recordLinkOpen(url, resourceName, repositoryFileId);
                      },
                      openInApp: () => {
                        if (c.id) {
                          navigate(`/app/mobile/focus-areas/resource/${c.id}`, {
                            state: { title: c.name || c.title || 'Resource' },
                          });
                        }
                      },
                      openInNewTab: (url) => window.open(url, '_blank', 'noopener'),
                    });
                  };

                  return (
                    <ResourceRow
                      key={i}
                      role="button"
                      tabIndex={0}
                      onClick={(e) => handleRowAction(e)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleRowAction(e);
                        }
                      }}
                      aria-label={`Open ${c.name || c.title || 'resource'}`}
                    >
                      <ResourceIcon>
                        {isYouTubeResource(c)
                          ? getFileTypeIcon(FileTypes.MP4)
                          : (typeof c.type === 'number') ? getFileTypeIcon(c.type) : <Icon name="file outline" />}
                      </ResourceIcon>
                      <ResourceInfo>
                        <H4>{c.name || c.title || '—'}</H4>
                        {isYouTubeResource(c) ? (
                          <YouTubeMetaInline data-testid="youtube-meta-label">
                            <Icon name="youtube" color="red" style={{ margin: 0 }} />
                            <span>YouTube</span>
                          </YouTubeMetaInline>
                        ) : (
                          <ResourceMeta>{c.object_key || c.key || ''}</ResourceMeta>
                        )}
                      </ResourceInfo>
                    </ResourceRow>
                  );
                })}
              </ResourceList>
            ) : (
              <BodyText className="text-sm text-gray-500">No resources available</BodyText>
            )}
          </Card>
        </SectionBlock>

        {isFromPillars ? (
          <SectionBlock>
            <ButtonRow>
              <StyledButton
                label="Back"
                color="gray"
                width="100%"
                height="46px"
                onClick={handleBack}
              />
              <StyledButton
                label={isItemAdded ? 'Added to focus plan' : 'Add to focus areas'}
                color="blue"
                width="100%"
                height="46px"
                onClick={handleAddToFocusAreas}
                disabled={isItemAdded}
              />
            </ButtonRow>
          </SectionBlock>
        ) : null}
      </MobilePageSection>
    </MobilePageContent>
  );
};

export default MobileFocusAreaDetail;

const TopRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
`;

const SmallCaption = styled.div`
  color: #64748b;
  font-size: 13px;
  margin-top: 6px;
`;

const SectionBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const Card = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 20px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Tile = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const IconCircle = styled.div`
  width: 44px;
  height: 44px;
  display: grid;
  place-items: center;
  border-radius: 999px;
`;

const FieldBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ResourceList = styled.div`
  display: grid;
  gap: 12px;
`;

const ResourceRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: #f8fafc;
  border-radius: 16px;
  cursor: pointer;
`;

const ResourceIcon = styled.div`
  display: grid;
  place-items: center;
  width: 36px;
  height: 36px;
  color: #0f172a;
`;

const ResourceInfo = styled.div`
  min-width: 0;
`;

const ButtonRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  width: 100%;
`;

const ResourceMeta = styled.div`
  color: #64748b;
  font-size: 12px;
`;

const YouTubeMetaInline = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #64748b;
`;
